/**
 * `OsLida` vira OS no banco.
 *
 * Três regras governam este arquivo (PRD §4.4, RN-001 a RN-005):
 *
 *  1. **A chave é o número impresso.** Uma OS existe uma vez, com N versões.
 *  2. **Mesmo número com conteúdo diferente é versão nova** — e a versão nova
 *     não pode apagar o que já foi apontado. Etapa não iniciada é substituída;
 *     etapa iniciada ou concluída é preservada e marcada para conferência.
 *  3. **Nada entra pela metade.** Ou a OS entra inteira, ou vai para a
 *     quarentena com o PDF visível e o motivo em português.
 */

import {
  instanteDaOs,
  novoId,
  type DeParaEstacao,
  type Estacao,
  type Os,
  type OsEntregaPrevista,
  type OsEtapa,
  type OsLida,
  type OsMaterial,
  type OsPlano,
  type OsVersao,
} from '@pcpgbu/dominio';
import { classificar } from './de-para.js';

export interface EntradaDaImportacao {
  osLida: OsLida;
  /** A OS que já existe com este número, quando existe. */
  existente: {
    os: Os;
    etapas: OsEtapa[];
    /** Ids de etapa que já têm evento — as que não podem ser substituídas. */
    etapasComApontamento: ReadonlySet<string>;
    versoes: OsVersao[];
  } | null;
  regras: readonly DeParaEstacao[];
  estacoes: readonly Estacao[];
  clienteId: string | null;
  importacaoId: string;
  pdfRef: string | null;
  imagemFacaRef: string | null;
  agora: string;
}

export type ResultadoImportacao =
  | { tipo: 'duplicada'; osNumero: number; versao: number }
  | {
      tipo: 'aplicada';
      novaOs: boolean;
      os: Os;
      versao: OsVersao;
      planos: OsPlano[];
      etapas: OsEtapa[];
      etapasRemovidas: string[];
      materiais: OsMaterial[];
      entregas: OsEntregaPrevista[];
      diff: string[];
      /** Etapas preservadas por já terem apontamento (RN-003). */
      etapasMarcadas: string[];
    };

export function importarOs(entrada: EntradaDaImportacao): ResultadoImportacao {
  const { osLida, existente, agora } = entrada;
  const c = osLida.cabecalho;

  // RN-002: mesmo conteúdo, nada a fazer.
  const jaAplicada = existente?.versoes.find((v) => v.hashTexto === osLida.hashTexto);
  if (jaAplicada) {
    return { tipo: 'duplicada', osNumero: c.osNumero, versao: jaAplicada.versao };
  }

  const versaoNumero = (existente?.os.versaoAtual ?? 0) + 1;
  const setorPorEstacao = new Map(entrada.estacoes.map((e) => [e.id, e.setorId]));

  const os: Os = {
    id: existente?.os.id ?? novoId(),
    numero: c.osNumero,
    versaoAtual: versaoNumero,
    clienteId: entrada.clienteId,
    clienteCurto: c.clienteCurto,
    quantidade: c.quantidade,
    produto: c.produto,
    emitidaEm: instanteDaOs(c.emissao.data, c.emissao.hora) ?? agora,
    emitidaPor: c.emissao.usuario,
    alteradaEm: c.alteracao ? instanteDaOs(c.alteracao.data, c.alteracao.hora) : null,
    alteradaPor: c.alteracao?.usuario ?? null,
    previsaoEntregaEm:
      (c.previsaoEntrega && instanteDaOs(c.previsaoEntrega.data, c.previsaoEntrega.hora)) ?? agora,
    orcamento: c.orcamento,
    contato: c.contato,
    transportadora: c.transportadora,
    observacoesGerais: c.observacoesGerais,
    entregaTipo: osLida.especificacoes.entrega,
    notaTipo: osLida.especificacoes.nota,
    arquivar: osLida.especificacoes.arquivar,
    ordemCompraCliente: osLida.especificacoes.ordemCompraCliente,
    acabamentoVinil: osLida.especificacoes.acabamentoAdesivoVinil,
    ultimaOsClienteEm: osLida.especificacoes.ultimaOs,
    concluidaNoErp: c.carimbo !== null && /conclu/i.test(c.carimbo),

    // O que a produção decidiu não é reimportado: status, prioridade e datas de
    // fechamento pertencem ao PCP GBU, não ao PDF.
    status: existente?.os.status ?? 'em_fila',
    prioridadeEm: existente?.os.prioridadeEm ?? null,
    prontaEm: existente?.os.prontaEm ?? null,
    concluidaEm: existente?.os.concluidaEm ?? null,
    canceladaEm: existente?.os.canceladaEm ?? null,
    motivoCancelamento: existente?.os.motivoCancelamento ?? null,

    pdfRef: entrada.pdfRef ?? existente?.os.pdfRef ?? null,
    imagemFacaRef: entrada.imagemFacaRef ?? existente?.os.imagemFacaRef ?? null,
  };

  const planos: OsPlano[] = osLida.planos.map((p) => ({
    id: novoId(),
    osNumero: c.osNumero,
    numero: p.numero,
    tipo: p.tipo,
    cores: p.cores,
    paginas: p.paginas,
    laminas: p.laminas,
    formatoFinal: p.formatoFinal,
    formatoFinalUnidade: p.formatoFinalUnidade,
    pecasPorFolha: p.pecasPorFolha,
    materialCodigo: p.materialCodigo,
    materialFormato: p.materialFormato,
    montagem: p.montagem,
    tiraRetira: p.tiraRetira,
    impressoraCodigo: p.impressora,
    abertura: p.abertura,
    refileSangria: p.refileSangria,
    formatoImpressao: p.formatoImpressao,
    faca: p.faca,
    lombada: p.lombada,
    solda: p.solda,
    outrosDetalhes: p.outrosDetalhes,
  }));

  // ---- etapas: casar as novas com as que já existem -------------------

  const antigas = existente?.etapas ?? [];
  const comApontamento = existente?.etapasComApontamento ?? new Set<string>();
  const usadas = new Set<string>();
  const etapas: OsEtapa[] = [];
  const etapasMarcadas: string[] = [];

  for (const lida of osLida.etapas) {
    const classificacao = classificar(
      { secao: lida.secao, equipamentoOs: lida.equipamento, nomeEtapa: lida.nome },
      entrada.regras,
    );

    const antiga = casarEtapa(antigas, usadas, lida.secao, lida.sequencia, lida.nome);
    if (antiga) usadas.add(antiga.id);

    const preservar = antiga !== null && comApontamento.has(antiga.id);

    const etapa: OsEtapa = {
      id: antiga?.id ?? novoId(),
      osNumero: c.osNumero,
      secao: lida.secao,
      sequencia: lida.sequencia,
      nomeOs: lida.nome,
      plano: lida.plano,
      equipamentoOs: lida.equipamento,
      estacaoId: classificacao.estacaoId,
      setorId: classificacao.estacaoId
        ? (setorPorEstacao.get(classificacao.estacaoId) ?? null)
        : null,
      tipoLinha: classificacao.tipoLinha,
      aClassificar: classificacao.aClassificar,
      impressoesPrevistas: lida.impressoes,
      unidade: lida.impressoesUnidade,
      tempoPrevistoSeg: lida.tempoPrevistoSeg,
      observacoesOs: lida.observacoes,
      origem: antiga?.origem ?? 'os',
      etapaOrigemId: antiga?.etapaOrigemId ?? null,
      versaoCriada: antiga?.versaoCriada ?? versaoNumero,
      // RN-003: a etapa que já foi tocada não pode mudar em silêncio.
      marcadaVerificar: preservar && mudou(antiga, lida),
      ordemManual: antiga?.ordemManual ?? null,
    };

    if (etapa.marcadaVerificar) etapasMarcadas.push(etapa.id);
    etapas.push(etapa);
  }

  // Etapas manuais e retrabalhos não vêm do PDF: seguem vivos.
  for (const antiga of antigas) {
    if (usadas.has(antiga.id)) continue;
    if (antiga.origem !== 'os') {
      etapas.push(antiga);
      usadas.add(antiga.id);
    }
  }

  // O que a versão nova não tem mais e ninguém apontou: sai.
  const etapasRemovidas = antigas
    .filter((a) => !usadas.has(a.id) && !comApontamento.has(a.id))
    .map((a) => a.id);

  // O que sumiu do PDF mas já tem apontamento fica, marcado.
  for (const antiga of antigas) {
    if (usadas.has(antiga.id) || etapasRemovidas.includes(antiga.id)) continue;
    etapas.push({ ...antiga, marcadaVerificar: true });
    etapasMarcadas.push(antiga.id);
  }

  const materiais: OsMaterial[] = osLida.materiais.map((m, ordem) => ({
    id: novoId(),
    osNumero: c.osNumero,
    ordem,
    codigo: m.codigo,
    nome: m.nome,
    plano: m.plano,
    quantidade: m.quantidade,
    unidade: m.unidade,
    quebraPct: m.quebraPct,
    observacoes: m.observacoes,
  }));

  const entregas: OsEntregaPrevista[] = osLida.entregas.map((bruto) => ({
    id: novoId(),
    osNumero: c.osNumero,
    bruto,
  }));

  const diff = existente ? calcularDiff(existente.os, existente.etapas, os, etapas) : [];

  const versao: OsVersao = {
    id: novoId(),
    osNumero: c.osNumero,
    versao: versaoNumero,
    hashTexto: osLida.hashTexto,
    importacaoId: entrada.importacaoId,
    versaoLeitor: osLida.versaoLeitor,
    diffResumo: diff,
    aplicadaEm: agora,
  };

  return {
    tipo: 'aplicada',
    novaOs: existente === null,
    os,
    versao,
    planos,
    etapas,
    etapasRemovidas,
    materiais,
    entregas,
    diff,
    etapasMarcadas,
  };
}

/**
 * Casa uma etapa nova com a antiga.
 *
 * Primeiro pelo nome dentro da seção — é o que sobrevive a uma etapa ser
 * inserida no meio da lista. Só depois pela posição, para o caso de a etapa
 * ter sido renomeada.
 */
function casarEtapa(
  antigas: readonly OsEtapa[],
  usadas: ReadonlySet<string>,
  secao: string,
  sequencia: number,
  nome: string,
): OsEtapa | null {
  const livres = antigas.filter((a) => !usadas.has(a.id) && a.secao === secao && a.origem === 'os');
  return (
    livres.find((a) => a.nomeOs === nome) ??
    livres.find((a) => a.sequencia === sequencia) ??
    null
  );
}

function mudou(antiga: OsEtapa, nova: OsLida['etapas'][number]): boolean {
  return (
    antiga.nomeOs !== nova.nome ||
    antiga.equipamentoOs !== nova.equipamento ||
    antiga.tempoPrevistoSeg !== nova.tempoPrevistoSeg ||
    antiga.impressoesPrevistas !== nova.impressoes ||
    antiga.observacoesOs !== nova.observacoes ||
    antiga.sequencia !== nova.sequencia
  );
}

/** O que mudou, em português, para a tela e para o alerta. */
export function calcularDiff(
  antesOs: Os,
  antesEtapas: readonly OsEtapa[],
  depoisOs: Os,
  depoisEtapas: readonly OsEtapa[],
): string[] {
  const linhas: string[] = [];

  if (antesOs.previsaoEntregaEm !== depoisOs.previsaoEntregaEm) {
    linhas.push(
      `Previsão de entrega: ${curta(antesOs.previsaoEntregaEm)} → ${curta(depoisOs.previsaoEntregaEm)}`,
    );
  }
  if (antesOs.quantidade !== depoisOs.quantidade) {
    linhas.push(`Quantidade: ${antesOs.quantidade} → ${depoisOs.quantidade}`);
  }
  if (antesOs.produto !== depoisOs.produto) {
    linhas.push(`Produto: "${antesOs.produto}" → "${depoisOs.produto}"`);
  }
  if (antesOs.entregaTipo !== depoisOs.entregaTipo) {
    linhas.push(`Entrega: ${antesOs.entregaTipo ?? '—'} → ${depoisOs.entregaTipo ?? '—'}`);
  }
  if (antesOs.observacoesGerais.join('\n') !== depoisOs.observacoesGerais.join('\n')) {
    linhas.push('Observações gerais mudaram.');
  }

  const antesPorId = new Map(antesEtapas.map((e) => [e.id, e]));
  const depoisPorId = new Map(depoisEtapas.map((e) => [e.id, e]));

  for (const e of depoisEtapas) {
    const a = antesPorId.get(e.id);
    if (!a) {
      linhas.push(`Etapa adicionada: "${e.nomeOs}"`);
      continue;
    }
    if (a.tempoPrevistoSeg !== e.tempoPrevistoSeg) {
      linhas.push(
        `"${e.nomeOs}": tempo previsto ${hhmm(a.tempoPrevistoSeg)} → ${hhmm(e.tempoPrevistoSeg)}`,
      );
    }
    if (a.equipamentoOs !== e.equipamentoOs) {
      linhas.push(`"${e.nomeOs}": equipamento ${a.equipamentoOs ?? '—'} → ${e.equipamentoOs ?? '—'}`);
    }
    if (a.impressoesPrevistas !== e.impressoesPrevistas) {
      linhas.push(
        `"${e.nomeOs}": quantidade ${a.impressoesPrevistas ?? '—'} → ${e.impressoesPrevistas ?? '—'}`,
      );
    }
    if (a.observacoesOs !== e.observacoesOs) {
      linhas.push(`"${e.nomeOs}": observação mudou`);
    }
  }

  for (const a of antesEtapas) {
    if (!depoisPorId.has(a.id)) linhas.push(`Etapa removida: "${a.nomeOs}"`);
  }

  return linhas;
}

function curta(iso: string): string {
  const d = new Date(Date.parse(iso) - 3 * 3600_000);
  return `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function hhmm(seg: number | null): string {
  if (seg === null) return '—';
  return `${Math.floor(seg / 3600)}:${String(Math.floor((seg % 3600) / 60)).padStart(2, '0')}`;
}
